import { useEffect, useState } from "react";
import type { Release } from "@edufadder/shared-types";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

export function ReleasesPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [newVersion, setNewVersion] = useState("");
  const [error, setError] = useState<string | null>(null);

  function load(): void {
    api
      .listReleases()
      .then(setReleases)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load releases."));
  }

  useEffect(load, []);

  async function handlePublish(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await api.publishRelease(newVersion);
      setNewVersion("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't publish this release.");
    }
  }

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>Releases</h1>
      <p style={s.mutedText}>
        A release is an immutable, checksummed snapshot of which evidence version is currently published for each
        claim. Publishing a new release promotes every currently-approved evidence version to published, superseding
        whatever it replaces.
      </p>

      {hasPermission("releases:publish") && (
        <form onSubmit={(e) => void handlePublish(e)} style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <input
            style={{ ...s.input, marginBottom: 0 }}
            placeholder="e.g. 2026.11.0"
            required
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
          />
          <button type="submit" style={s.buttonPrimary}>
            Publish release
          </button>
        </form>
      )}
      {error && <p style={s.errorText}>{error}</p>}

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Version</th>
            <th style={s.th}>Published</th>
            <th style={s.th}>Checksum</th>
          </tr>
        </thead>
        <tbody>
          {(releases ?? []).map((r) => (
            <tr key={r.id}>
              <td style={s.td}>{r.version}</td>
              <td style={s.td}>{new Date(r.published_at).toLocaleString()}</td>
              <td style={{ ...s.td, fontFamily: "monospace", fontSize: 11 }}>{r.checksum.slice(0, 16)}…</td>
            </tr>
          ))}
        </tbody>
      </table>
      {releases !== null && releases.length === 0 && <p style={s.mutedText}>No releases published yet.</p>}
    </div>
  );
}
